import type { FormEvent } from 'react'
import { PageHeader } from '@/components/layout/page-header'
import { InquiryOwnerPicker } from '@/src/components/InquiryOwnerPicker'
import { CancelInquiryDialog } from '@/src/components/CancelInquiryDialog'
import { InquiryCancelledBanner } from '@/src/components/InquiryCancelledBanner'
import { InquiryTasks } from '@/src/components/InquiryTasks'
import { isCustomerLogin } from '@/src/lib/roles'
import { canCancelInquiry } from '@/lib/quick-edit'
import { inquiryTitle } from './inquirySummary'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Ban,
  CheckCircle2,
  Circle,
  ExternalLink,
  Factory,
  History,
  ImageIcon,
  Loader2,
  Mail,
  MessageSquare,
  PackageCheck,
  PackagePlus,
  Pencil,
  PlusCircle,
  Save,
  Trash2,
  XCircle,
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/status-badge'
import { FactCard } from '@/components/ui/stat'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api/client'
import { customerIdentityValue } from '@/lib/customer-identity'
import { InquiryCustomerAssociation } from '@/src/components/InquiryCustomerAssociation'
import { uploadInvoiceAsset, uploadProductImage } from '@/lib/storage/images'
import {
  INQUIRY_PHASE_STEPS,
  canTransitionInquiryPhase,
  defaultInquiryNextAction,
  defaultTaskForInquiryPhase,
  inquiryPhaseStep,
  inquiryPhaseTransitionChoices,
  isControlledInquiryNextAction,
  workflowOperationalStateTransitionChoices,
} from '@/lib/inquiry-workflow'
import { cn, formatCurrency } from '@/lib/utils'
import { CommunicationAttachmentPicker } from '@/src/components/CommunicationAttachmentPicker'
import { ClientFiles } from '@/src/components/ClientFiles'
import { InquiryCargoCard } from '@/src/components/InquiryCargoCard'
import { ErrorState } from '@/src/components/ErrorState'
import { HintedLabel } from '@/src/components/FieldHint'
import { FIELD_HELP } from '@/src/lib/onboarding/field-help'
import { HelmCcSelector } from '@/src/components/HelmCcSelector'
import { LoadingState } from '@/src/components/LoadingState'
import { ReplyDrafts } from '@/src/components/ReplyDrafts'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'
import { DueDateEditor } from '@/src/components/DueDateEditor'
import { ShipmentEditor } from '@/src/components/ShipmentEditor'
import { customerGreetingName } from '@/lib/customer-messages'
import { communicationActivity, workflowEventActivity, type NameOf } from '@/lib/record-activity'
import {
  Translatable,
  TranslatableMessage,
  TranslateToggle,
  Translated,
  TranslationBelow,
} from '@/src/components/translation/Translatable'
import type {
  HelmCommunication,
  HelmOutboundCommunicationAttachment,
  HelmInquiryPriority,
  HelmInquiryStatus,
  Inquiry,
  InquiryWorkflowPhase,
  Profile,
  WorkflowOperationalState,
  WorkflowTaskStatus,
} from '@/lib/api/types'

const priorities: HelmInquiryPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT']
const quoteCurrencies = ['USD', 'CNY', 'UGX', 'KES', 'EUR', 'GBP'] as const
const templates = [
  'HELM_INQUIRY_UPDATE',
  'REQUEST_MISSING_DOCS',
  'QUOTE_SENT',
  'PAYMENT_RECEIVED',
  'SHIPMENT_STATUS',
  'CUSTOMS_STATUS',
  'DELIVERY_READY',
  'ISSUE_BLOCKER',
] as const

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function centsToMajor(value: number | null | undefined) {
  return value == null ? null : value / 100
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleString()
}

function toDateTimeLocal(value: string | null | undefined) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const timezoneOffset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16)
}

function normalizeWhatsAppPhone(phone: string | null | undefined) {
  return phone?.replace(/\D/g, '') || ''
}

function customerName(inquiry: Inquiry) {
  return inquiry.customer?.company_name || inquiry.customer?.name || 'Customer not linked'
}

function serviceTypeLabel(inquiry: Inquiry) {
  const services = inquiry.service_types?.length ? inquiry.service_types : [inquiry.service_type]
  return services.map(formatLabel).join(', ')
}

function supplierField(inquiry: Inquiry, key: string) {
  const value = inquiry.supplier_details?.[key]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

interface SupplierOption {
  id: string
  name: string
  email?: string | null
  phone?: string | null
  address?: string | null
  quote_status?: string | null
  quoted_amount?: number | null
  currency?: string | null
  proforma_url?: string | null
  notes?: string | null
  selected?: boolean
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function optionalString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function optionalNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function optionFromRecord(value: unknown, index: number): SupplierOption | null {
  const record = asRecord(value)
  const name = optionalString(record.name)
  if (!name) return null

  return {
    id: optionalString(record.id) ?? `supplier-${index + 1}`,
    name,
    email: optionalString(record.email),
    phone: optionalString(record.phone),
    address: optionalString(record.address),
    quote_status: optionalString(record.quote_status) ?? 'PROSPECT',
    quoted_amount: optionalNumber(record.quoted_amount),
    currency: optionalString(record.currency) ?? 'USD',
    proforma_url: optionalString(record.proforma_url),
    notes: optionalString(record.notes),
    selected: Boolean(record.selected),
  }
}

function supplierOptionsFromInquiry(inquiry: Inquiry): SupplierOption[] {
  const details = asRecord(inquiry.supplier_details)
  const rawOptions = Array.isArray(details.supplier_options) ? details.supplier_options : []
  const options = rawOptions
    .map(optionFromRecord)
    .filter((option): option is SupplierOption => Boolean(option))

  if (options.length) return options

  const fallback = optionFromRecord(
    {
      id: 'primary-supplier',
      name: details.name,
      email: details.email,
      phone: details.phone,
      address: details.address,
      quote_status: details.quote_status,
      quoted_amount: details.quoted_amount,
      currency: details.currency,
      proforma_url: details.proforma_url,
      notes: details.notes,
      selected: details.selected,
    },
    0,
  )

  return fallback ? [fallback] : []
}

function supplierDetailsWithOptions(inquiry: Inquiry, options: SupplierOption[]) {
  const selected = options.find((option) => option.selected) ?? null
  const primary = selected ?? options[0] ?? null
  return {
    ...asRecord(inquiry.supplier_details),
    ...(primary
      ? {
          name: primary.name,
          email: primary.email ?? null,
          phone: primary.phone ?? null,
          address: primary.address ?? null,
        }
      : {}),
    selected_supplier_id: selected?.id ?? null,
    supplier_options: options,
  }
}

function selectedSupplierOption(inquiry: Inquiry) {
  const options = supplierOptionsFromInquiry(inquiry)
  return options.find((option) => option.selected) ?? options[0] ?? null
}

function communicationCcRecipients(communication: HelmCommunication) {
  const cc = communication.metadata?.cc
  return Array.isArray(cc)
    ? cc.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
}

interface InquiryActivity {
  id: string
  title: string
  description?: string
  timestamp?: string | null
  label?: string
}

function parseActivityTimestamp(value: string | null | undefined) {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
}

function parseWorkflowActivities(notes: string | null | undefined): InquiryActivity[] {
  if (!notes) return []
  const activities: InquiryActivity[] = []
  notes.split(/\n{2,}/).forEach((block, index) => {
    const lines = block
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    if (!lines[0]?.startsWith('Workflow transition -')) return
    const rawTimestamp = lines[0].replace('Workflow transition -', '').trim()
    const phase = lines
      .find((line) => line.startsWith('Phase:'))
      ?.replace('Phase:', '')
      .trim()
    const nextAction = lines
      .find((line) => line.startsWith('Next action:'))
      ?.replace('Next action:', '')
      .trim()
    const comment = lines
      .find((line) => line.startsWith('Comment:'))
      ?.replace('Comment:', '')
      .trim()
    activities.push({
      id: `workflow-${index}`,
      title: phase ? `Workflow: ${phase}` : 'Workflow update',
      description: [comment, nextAction ? `Next: ${nextAction}` : null].filter(Boolean).join('\n'),
      timestamp: parseActivityTimestamp(rawTimestamp),
      label: 'Workflow',
    })
  })
  return activities
}

function buildInquiryActivities(
  inquiry: Inquiry,
  communications: HelmCommunication[],
  nameOf: NameOf,
  options: { taskTitles?: boolean } = {},
): InquiryActivity[] {
  const workflowActivities = inquiry.workflow_events.map((event) => ({
    id: `workflow-${event.id}`,
    ...workflowEventActivity(event, nameOf, options),
    timestamp: event.occurred_at,
    label: 'Workflow',
  }))
  const documentActivities = inquiry.documents.map((document, index) => ({
    id: `document-${index}`,
    // A client's emailed file links to the API, not somewhere a person can open.
    title:
      document.type === 'CLIENT_ATTACHMENT'
        ? 'File from the client'
        : `Document: ${formatLabel(document.type)}`,
    description:
      document.type === 'CLIENT_ATTACHMENT'
        ? document.file_name || 'Attachment'
        : [document.file_name, document.file_url].filter(Boolean).join('\n'),
    timestamp: document.uploaded_at ?? null,
    label: 'Document',
  }))
  // Filed documents keep their files private, so only the name is shown here.
  const filedDocumentActivities = (inquiry.filed_documents ?? []).map((document) => ({
    id: `filed-document-${document.id}`,
    title: `Document: ${formatLabel(document.type)}`,
    description: document.file_name || formatLabel(document.type),
    timestamp: document.created_at,
    label: 'Document',
  }))
  const communicationActivities = communications.map((communication) => ({
    id: `communication-${communication.id}`,
    ...communicationActivity(communication),
  }))

  return [
    {
      id: 'created',
      title: 'Inquiry created',
      description: `${customerName(inquiry)} · ${serviceTypeLabel(inquiry)}`,
      timestamp: inquiry.created_at,
      label: 'System',
    },
    ...(workflowActivities.length ? workflowActivities : parseWorkflowActivities(inquiry.notes)),
    ...documentActivities,
    ...filedDocumentActivities,
    ...communicationActivities,
  ].sort((first, second) => {
    const firstTime = first.timestamp ? new Date(first.timestamp).getTime() : 0
    const secondTime = second.timestamp ? new Date(second.timestamp).getTime() : 0
    return secondTime - firstTime
  })
}

function isAdmin(profile: Profile | null | undefined) {
  return Boolean(
    profile?.is_admin ||
    profile?.profile_role === 'admin' ||
    profile?.helm_roles?.includes('admin'),
  )
}

function UpdateInquiryPanel({
  inquiry,
  profile,
  onChanged,
}: {
  inquiry: Inquiry
  profile: Profile | null
  onChanged: () => void
}) {
  const admin = isAdmin(profile)
  const activeTask =
    inquiry.tasks.find((task) => task.status === 'IN_PROGRESS') ??
    inquiry.tasks.find((task) => task.status === 'TODO') ??
    null
  const [phase, setPhase] = useState<InquiryWorkflowPhase>(inquiry.workflow_phase)
  const [operationalState, setOperationalState] = useState<WorkflowOperationalState>(
    inquiry.operational_state,
  )
  const [priority, setPriority] = useState<HelmInquiryPriority>(inquiry.priority)
  const [taskTitle, setTaskTitle] = useState(
    activeTask?.title ?? defaultTaskForInquiryPhase(inquiry.workflow_phase),
  )
  const [taskStatus, setTaskStatus] = useState<WorkflowTaskStatus>(activeTask?.status ?? 'TODO')
  const [taskDueAt, setTaskDueAt] = useState(toDateTimeLocal(activeTask?.due_at))
  const [transitionComment, setTransitionComment] = useState('')
  const [notes, setNotes] = useState(inquiry.notes || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const currentStep = inquiryPhaseStep(phase)
  const phaseIndex = INQUIRY_PHASE_STEPS.findIndex((item) => item.phase === phase)
  const canCompleteFromPersistedState = inquiry.operational_state === 'ACTIVE'
  const phaseChoices = inquiryPhaseTransitionChoices(inquiry.workflow_phase).filter(
    (option) =>
      option !== 'COMPLETED' || option === inquiry.workflow_phase || canCompleteFromPersistedState,
  )
  // Cancelling and reopening record why, so they are actions of their own.
  const operationalStateChoices =
    inquiry.operational_state === 'CANCELLED'
      ? (['CANCELLED'] as WorkflowOperationalState[])
      : workflowOperationalStateTransitionChoices(inquiry.operational_state).filter(
          (option) => option !== 'CANCELLED',
        )
  const [cancelling, setCancelling] = useState(false)

  function handlePhaseChange(nextPhase: InquiryWorkflowPhase) {
    if (!canTransitionInquiryPhase(inquiry.workflow_phase, nextPhase)) return
    setPhase(nextPhase)
    if (!activeTask || taskTitle === defaultTaskForInquiryPhase(inquiry.workflow_phase)) {
      setTaskTitle(defaultTaskForInquiryPhase(nextPhase))
    }
    if (nextPhase === 'COMPLETED') {
      setOperationalState('COMPLETED')
      setTaskStatus('DONE')
    } else if (operationalState === 'COMPLETED') {
      setOperationalState('ACTIVE')
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const trimmedTaskTitle = taskTitle.trim()
    if (!trimmedTaskTitle) {
      setError('A next task is required.')
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)

    try {
      await apiClient.updateInquiry(inquiry.id, {
        workflow_phase: phase,
        operational_state: operationalState,
        priority,
        next_task: {
          id: activeTask?.id,
          title: trimmedTaskTitle,
          status: taskStatus,
          priority,
          assignee_user_id: activeTask?.assignee_user_id ?? inquiry.owner_user_id ?? null,
          due_at: taskDueAt ? new Date(taskDueAt).toISOString() : null,
        },
        transition_comment: transitionComment.trim() || null,
        notes: notes.trim() || null,
      })
      setTransitionComment('')
      setMessage('Workflow updated.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update inquiry')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card data-tour="inquiry-workflow">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Workflow</CardTitle>
            <CardDescription>
              Phase, operating state, and the next accountable task.
            </CardDescription>
          </div>
          <StatusBadge status={operationalState} />
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {/* A phone shows the five steps as one bar; the Phase field below moves it. */}
          <div className="space-y-1.5 sm:hidden">
            <ol aria-label="Workflow phase" className="flex gap-1">
              {INQUIRY_PHASE_STEPS.map((step, index) => (
                <li
                  key={step.phase}
                  aria-current={step.phase === phase ? 'step' : undefined}
                  className={cn(
                    'h-1.5 flex-1 rounded-full',
                    index <= phaseIndex ? 'bg-primary' : 'bg-muted',
                  )}
                >
                  <span className="sr-only">{step.title}</span>
                </li>
              ))}
            </ol>
            <p className="text-sm">
              <span className="font-medium">{currentStep.title}</span>
              <span className="text-muted-foreground">
                {' '}
                · step {phaseIndex + 1} of {INQUIRY_PHASE_STEPS.length}
              </span>
            </p>
          </div>
          <div className="hidden gap-2 sm:grid sm:grid-cols-5">
            {INQUIRY_PHASE_STEPS.map((step, index) => {
              const active = step.phase === phase
              const reachable =
                canTransitionInquiryPhase(inquiry.workflow_phase, step.phase) &&
                (step.phase !== 'COMPLETED' ||
                  step.phase === inquiry.workflow_phase ||
                  canCompleteFromPersistedState)
              const completed =
                index < INQUIRY_PHASE_STEPS.findIndex((item) => item.phase === phase)
              return (
                <button
                  key={step.phase}
                  type="button"
                  disabled={!admin || saving || !reachable}
                  onClick={() => handlePhaseChange(step.phase)}
                  className={`min-h-10 rounded-md border px-3 py-2 text-left transition-colors ${active ? 'border-primary bg-primary/5' : reachable ? 'bg-background hover:border-primary/60 hover:bg-muted/40' : 'cursor-not-allowed bg-muted/30 opacity-60'}`}
                  aria-pressed={active}
                >
                  <span className="flex items-center gap-2">
                    {active || completed ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                    ) : (
                      <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="text-sm font-medium">{step.title}</span>
                  </span>
                </button>
              )
            })}
          </div>
          <p className="text-xs text-muted-foreground">{currentStep.description}</p>

          <div className="grid gap-3 md:grid-cols-3 md:gap-4">
            <div className="space-y-2">
              <HintedLabel plain htmlFor="inquiry-phase" hint={FIELD_HELP.inquiryPhase}>
                Phase
              </HintedLabel>
              <Select
                value={phase}
                onValueChange={(value) => handlePhaseChange(value as InquiryWorkflowPhase)}
                disabled={!admin || saving}
              >
                <SelectTrigger id="inquiry-phase" className="max-md:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {phaseChoices.map((option) => {
                    const step = inquiryPhaseStep(option)
                    return (
                      <SelectItem key={option} value={option}>
                        {step.title}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <HintedLabel plain htmlFor="operational-state" hint={FIELD_HELP.operationalState}>
                State
              </HintedLabel>
              <Select
                value={operationalState}
                onValueChange={(value) => setOperationalState(value as WorkflowOperationalState)}
                disabled={!admin || saving}
              >
                <SelectTrigger id="operational-state" className="max-md:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operationalStateChoices.map((option) => (
                    <SelectItem key={option} value={option}>
                      {formatLabel(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={priority}
                onValueChange={(value) => setPriority(value as HelmInquiryPriority)}
                disabled={!admin || saving}
              >
                <SelectTrigger id="priority" className="max-md:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((option) => (
                    <SelectItem key={option} value={option}>
                      {formatLabel(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <HintedLabel plain htmlFor="next-task" hint={FIELD_HELP.nextTask}>
                Next Task
              </HintedLabel>
              <Input
                id="next-task"
                value={taskTitle}
                onChange={(event) => setTaskTitle(event.target.value)}
                disabled={!admin || saving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-status">Task Status</Label>
              <Select
                value={taskStatus}
                onValueChange={(value) => setTaskStatus(value as WorkflowTaskStatus)}
                disabled={!admin || saving}
              >
                <SelectTrigger id="task-status" className="max-md:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['TODO', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as WorkflowTaskStatus[]).map(
                    (option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-due-at">Due</Label>
              <Input
                id="task-due-at"
                type="datetime-local"
                value={taskDueAt}
                onChange={(event) => setTaskDueAt(event.target.value)}
                disabled={!admin || saving}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="transition-comment">Update Comment</Label>
              <Textarea
                id="transition-comment"
                rows={2}
                value={transitionComment}
                onChange={(event) => setTransitionComment(event.target.value)}
                disabled={!admin || saving}
                placeholder="Record why the phase, state, or task changed."
              />
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!admin || saving}
              />
              <TranslationBelow text={notes} context="note" label="the internal notes" />
            </div>
          </div>

          {admin ? (
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </Button>
              {canCancelInquiry(inquiry) && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={saving}
                  onClick={() => setCancelling(true)}
                >
                  <Ban className="h-4 w-4" />
                  Cancel inquiry…
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Admin access is required to update inquiry workflow fields.
            </p>
          )}
        </form>
        {cancelling && (
          <CancelInquiryDialog
            open
            onOpenChange={(open) => !open && setCancelling(false)}
            inquiry={inquiry}
            label={inquiryTitle(inquiry)}
            onConfirm={async (reason) => {
              await apiClient.cancelInquiry(inquiry.id, reason)
              onChanged()
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}

function InquiryProductsPanel({
  inquiry,
  profile,
  onChanged,
}: {
  inquiry: Inquiry
  profile: Profile | null
  onChanged: () => void
}) {
  const admin = isAdmin(profile)
  const products = inquiry.product_list
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [unitPrice, setUnitPrice] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [sourcingUrl, setSourcingUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [hsCode, setHsCode] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function resetProductForm() {
    setEditingIndex(null)
    setName('')
    setDescription('')
    setQuantity('1')
    setUnitPrice('')
    setCurrency('USD')
    setSourcingUrl('')
    setImageUrl('')
    setImageFile(null)
    setHsCode('')
    setNotes('')
    setError(null)
  }

  function openProductDialog(product?: Inquiry['product_list'][number], index?: number) {
    if (!product || index == null) {
      resetProductForm()
      setDialogOpen(true)
      return
    }

    setEditingIndex(index)
    setName(product.name)
    setDescription(product.description ?? '')
    setQuantity(String(product.quantity ?? 1))
    setUnitPrice(product.unit_price != null ? String(product.unit_price) : '')
    setCurrency(product.currency ?? 'USD')
    setSourcingUrl(product.sourcing_url ?? '')
    setImageUrl(product.image_url ?? '')
    setImageFile(null)
    setHsCode(product.hs_code ?? '')
    setNotes(product.notes ?? '')
    setError(null)
    setDialogOpen(true)
  }

  async function updateProducts(
    nextProducts: Inquiry['product_list'],
    comment: string,
    nextDocuments?: Inquiry['documents'],
  ) {
    const nextAction =
      inquiry.next_action && isControlledInquiryNextAction(inquiry.next_action)
        ? inquiry.next_action
        : defaultInquiryNextAction(inquiry.status)
    const activeTask =
      inquiry.tasks.find((task) => task.status === 'IN_PROGRESS') ??
      inquiry.tasks.find((task) => task.status === 'TODO')
    await apiClient.updateInquiry(inquiry.id, {
      product_list: nextProducts,
      ...(nextDocuments ? { documents: nextDocuments } : {}),
      next_task: {
        id: activeTask?.id,
        title: nextAction,
        status: activeTask?.status ?? 'TODO',
        priority: inquiry.priority,
        assignee_user_id: activeTask?.assignee_user_id ?? inquiry.owner_user_id ?? null,
        due_at: activeTask?.due_at ?? null,
      },
      transition_comment: comment,
    })
    onChanged()
  }

  async function saveProduct() {
    const productName = name.trim()
    if (!productName) {
      setError('Product name is required.')
      return
    }

    const parsedQuantity = Number(quantity)
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setError('Quantity must be greater than zero.')
      return
    }

    const parsedUnitPrice = unitPrice.trim() ? Number(unitPrice) : null
    if (parsedUnitPrice != null && (!Number.isFinite(parsedUnitPrice) || parsedUnitPrice < 0)) {
      setError('Unit price must be zero or greater.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      let nextImageUrl = imageUrl.trim() || null
      if (imageFile) {
        nextImageUrl = await uploadProductImage(imageFile)
      }

      const existing = editingIndex == null ? null : products[editingIndex]
      const nextProduct: Inquiry['product_list'][number] = {
        ...(existing ?? {}),
        name: productName,
        description: description.trim() || null,
        quantity: parsedQuantity,
        unit_price: parsedUnitPrice,
        currency,
        sourcing_url: sourcingUrl.trim() || null,
        image_url: nextImageUrl,
        hs_code: hsCode.trim() || null,
        notes: notes.trim() || null,
      }
      const nextProducts =
        editingIndex == null
          ? [...products, nextProduct]
          : products.map((product, index) => (index === editingIndex ? nextProduct : product))
      const nextDocuments =
        imageFile && nextImageUrl
          ? [
              ...inquiry.documents,
              {
                type: 'INQUIRY_PRODUCT_IMAGE',
                file_url: nextImageUrl,
                file_name: imageFile.name,
                uploaded_at: new Date().toISOString(),
                product_name: productName,
              },
            ]
          : undefined

      await updateProducts(
        nextProducts,
        `Requested item ${editingIndex == null ? 'added' : 'updated'}: ${productName}.`,
        nextDocuments,
      )
      resetProductForm()
      setDialogOpen(false)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save requested item')
    } finally {
      setSaving(false)
    }
  }

  async function deleteProduct(index: number) {
    if (products.length <= 1) {
      setError('An inquiry must keep at least one requested item.')
      return
    }

    const product = products[index]
    setSaving(true)
    setError(null)
    try {
      await updateProducts(
        products.filter((_, productIndex) => productIndex !== index),
        `Requested item removed: ${product?.name || 'unnamed item'}.`,
      )
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete requested item')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card data-tour="inquiry-items">
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Requested Items</CardTitle>
            <CardDescription>
              {products.length} product requirements captured for sourcing, quoting, and conversion.
            </CardDescription>
          </div>
          {admin && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => openProductDialog()}
              disabled={saving}
            >
              <PackagePlus className="h-4 w-4" />
              Add Item
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="overflow-x-auto rounded-xl border max-md:hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit</TableHead>
                <TableHead>HS</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product, index) => (
                <TableRow key={`${product.name}-${index}`}>
                  <TableCell className="min-w-[260px]">
                    <div className="flex items-start gap-3">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt=""
                          className="h-12 w-12 rounded-md border object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                          <ImageIcon className="h-5 w-5" />
                        </div>
                      )}
                      <Translated
                        texts={[product.name, product.description, product.notes]}
                        context="item"
                      >
                        {(translated, observe) => (
                          <div ref={observe}>
                            <div className="font-medium">{translated.text(0)}</div>
                            <div className="text-xs text-muted-foreground">
                              {product.description ? translated.text(1) : 'No description captured'}
                            </div>
                            {product.notes && (
                              <div className="mt-1 max-w-[420px] text-xs">{translated.text(2)}</div>
                            )}
                            <TranslateToggle
                              block={translated}
                              label={product.name}
                              className="mt-1"
                            />
                          </div>
                        )}
                      </Translated>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{product.quantity}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(
                      typeof product.unit_price === 'number' ? product.unit_price : null,
                      product.currency || 'USD',
                    )}
                  </TableCell>
                  <TableCell>{product.hs_code || 'Pending'}</TableCell>
                  <TableCell className="max-w-[220px] truncate">
                    {product.sourcing_url ? (
                      <a
                        className="text-primary underline-offset-4 hover:underline"
                        href={product.sourcing_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open source
                      </a>
                    ) : (
                      'Not set'
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        onClick={() => openProductDialog(product, index)}
                        disabled={!admin || saving}
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        onClick={() => deleteProduct(index)}
                        disabled={!admin || saving || products.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* On a phone, one card per item instead of a table wider than the screen. */}
        <ul aria-label="Requested items" className="divide-y rounded-xl border md:hidden">
          {products.map((product, index) => (
            <li key={`${product.name}-${index}`} className="space-y-3 p-3">
              <div className="flex items-start gap-3">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-md border object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="text-sm font-medium wrap-anywhere">{product.name}</p>
                  <p className="text-xs text-muted-foreground wrap-anywhere">
                    {product.description || 'No description captured'}
                  </p>
                  {product.notes && <p className="text-xs wrap-anywhere">{product.notes}</p>}
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <dt className="text-muted-foreground">Qty × unit</dt>
                  <dd className="font-medium">
                    {product.quantity} ×{' '}
                    {formatCurrency(
                      typeof product.unit_price === 'number' ? product.unit_price : null,
                      product.currency || 'USD',
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">HS code</dt>
                  <dd className="font-medium">{product.hs_code || 'Pending'}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-muted-foreground">Source</dt>
                  <dd>
                    {product.sourcing_url ? (
                      <a
                        className="text-primary underline-offset-4 hover:underline"
                        href={product.sourcing_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open source
                      </a>
                    ) : (
                      'Not set'
                    )}
                  </dd>
                </div>
              </dl>
              {admin && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => openProductDialog(product, index)}
                    disabled={saving}
                    aria-label={`Edit ${product.name}`}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => deleteProduct(index)}
                    disabled={saving || products.length <= 1}
                    aria-label={`Delete ${product.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
        {!admin && (
          <p className="text-sm text-muted-foreground">
            Admin access is required to manage requested items.
          </p>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {editingIndex == null ? 'Add Requested Item' : 'Edit Requested Item'}
              </DialogTitle>
              <DialogDescription>
                Capture the product details, source reference, and image evidence needed for
                sourcing and quotations.
              </DialogDescription>
            </DialogHeader>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="product-name">Product</Label>
                <Input
                  id="product-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="space-y-2">
                <HintedLabel plain htmlFor="product-hs" hint={FIELD_HELP.hsCode}>
                  HS Code
                </HintedLabel>
                <Input
                  id="product-hs"
                  value={hsCode}
                  onChange={(event) => setHsCode(event.target.value)}
                  disabled={saving}
                  placeholder="Pending"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product-quantity">Quantity</Label>
                <Input
                  id="product-quantity"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
                <div className="space-y-2">
                  <HintedLabel plain htmlFor="product-unit-price" hint={FIELD_HELP.unitPrice}>
                    Unit Price
                  </HintedLabel>
                  <Input
                    id="product-unit-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitPrice}
                    onChange={(event) => setUnitPrice(event.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product-currency">Currency</Label>
                  <Select value={currency} onValueChange={setCurrency} disabled={saving}>
                    <SelectTrigger id="product-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {quoteCurrencies.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="product-description">Description / Requirements</Label>
                <Textarea
                  id="product-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={saving}
                  rows={3}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <HintedLabel plain htmlFor="product-source" hint={FIELD_HELP.sourcingUrl}>
                  Sourcing URL
                </HintedLabel>
                <Input
                  id="product-source"
                  value={sourcingUrl}
                  onChange={(event) => setSourcingUrl(event.target.value)}
                  disabled={saving}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="product-image-url">Image URL</Label>
                <Input
                  id="product-image-url"
                  value={imageUrl}
                  onChange={(event) => setImageUrl(event.target.value)}
                  disabled={saving}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="product-image-file">Upload Product Image</Label>
                <Input
                  id="product-image-file"
                  type="file"
                  accept="image/*"
                  onChange={(event) => setImageFile(event.target.files?.[0] ?? null)}
                  disabled={saving}
                />
                {imageFile && <p className="text-xs text-muted-foreground">{imageFile.name}</p>}
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="product-notes">Notes</Label>
                <Textarea
                  id="product-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={saving}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="button" onClick={saveProduct} disabled={saving || !name.trim()}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

function SupplierOptionsPanel({
  inquiry,
  profile,
  onChanged,
}: {
  inquiry: Inquiry
  profile: Profile | null
  onChanged: () => void
}) {
  const admin = isAdmin(profile)
  const options = supplierOptionsFromInquiry(inquiry)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [quoteStatus, setQuoteStatus] = useState('PROSPECT')
  const [quotedAmount, setQuotedAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [proformaUrl, setProformaUrl] = useState('')
  const [quoteFile, setQuoteFile] = useState<File | null>(null)
  const [notes, setNotes] = useState('')
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const editingSupplier = editingSupplierId
    ? (options.find((option) => option.id === editingSupplierId) ?? null)
    : null

  function resetForm() {
    setName('')
    setEmail('')
    setPhone('')
    setAddress('')
    setQuoteStatus('PROSPECT')
    setQuotedAmount('')
    setCurrency('USD')
    setProformaUrl('')
    setQuoteFile(null)
    setNotes('')
    setEditingSupplierId(null)
  }

  function openSupplierDialog(option?: SupplierOption) {
    if (!option) {
      resetForm()
      setDialogOpen(true)
      return
    }

    setEditingSupplierId(option.id)
    setName(option.name)
    setEmail(option.email ?? '')
    setPhone(option.phone ?? '')
    setAddress(option.address ?? '')
    setQuoteStatus(option.quote_status ?? 'PROSPECT')
    setQuotedAmount(option.quoted_amount != null ? String(option.quoted_amount) : '')
    setCurrency(option.currency ?? 'USD')
    setProformaUrl(option.proforma_url ?? '')
    setQuoteFile(null)
    setNotes(option.notes ?? '')
    setDialogOpen(true)
  }

  async function saveOptions(
    nextOptions: SupplierOption[],
    nextAction: string,
    comment: string,
    nextStatus?: HelmInquiryStatus,
    nextDocuments?: Inquiry['documents'],
  ) {
    setSaving(true)
    setError(null)
    try {
      const activeTask =
        inquiry.tasks.find((task) => task.status === 'IN_PROGRESS') ??
        inquiry.tasks.find((task) => task.status === 'TODO')
      await apiClient.updateInquiry(inquiry.id, {
        ...(nextStatus && nextStatus !== inquiry.status ? { status: nextStatus } : {}),
        supplier_details: supplierDetailsWithOptions(inquiry, nextOptions),
        next_task: {
          id: activeTask?.id,
          title: nextAction,
          status: activeTask?.status ?? 'TODO',
          priority: inquiry.priority,
          assignee_user_id: activeTask?.assignee_user_id ?? inquiry.owner_user_id ?? null,
          due_at: activeTask?.due_at ?? null,
        },
        ...(nextDocuments ? { documents: nextDocuments } : {}),
        transition_comment: comment,
      })
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update supplier options')
    } finally {
      setSaving(false)
    }
  }

  async function saveSupplierOption() {
    const supplierName = name.trim()
    if (!supplierName) {
      setError('Supplier name is required.')
      return
    }

    let uploadedQuoteUrl = proformaUrl.trim() || null
    if (quoteFile) {
      setSaving(true)
      setError(null)
      try {
        uploadedQuoteUrl = await uploadInvoiceAsset(quoteFile)
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to upload supplier quote')
        setSaving(false)
        return
      }
      setSaving(false)
    }

    const nextOption: SupplierOption = {
      id: editingSupplierId ?? globalThis.crypto?.randomUUID?.() ?? `supplier-${Date.now()}`,
      name: supplierName,
      email: email.trim() || null,
      phone: phone.trim() || null,
      address: address.trim() || null,
      quote_status: quoteStatus,
      quoted_amount: quotedAmount.trim() ? Number(quotedAmount) : null,
      currency: currency.trim() || 'USD',
      proforma_url: uploadedQuoteUrl,
      notes: notes.trim() || null,
      selected: editingSupplier?.selected ?? false,
    }
    const nextOptions = editingSupplierId
      ? options.map((option) => (option.id === editingSupplierId ? nextOption : option))
      : [...options, nextOption]
    const nextDocuments =
      uploadedQuoteUrl &&
      (!editingSupplier || uploadedQuoteUrl !== editingSupplier.proforma_url || quoteFile)
        ? [
            ...inquiry.documents,
            {
              type: 'SUPPLIER_QUOTE',
              file_url: uploadedQuoteUrl,
              file_name: quoteFile?.name || `${supplierName} quotation`,
              uploaded_at: new Date().toISOString(),
              supplier_option_id: nextOption.id,
              supplier_name: supplierName,
              quote_status: quoteStatus,
            },
          ]
        : undefined
    await saveOptions(
      nextOptions,
      inquiry.status === 'SUBMITTED'
        ? 'Distill requirements and identify supplier options'
        : 'Request factory quotations from shortlisted suppliers',
      `Supplier option ${editingSupplierId ? 'updated' : 'added'}: ${nextOption.name}${nextOption.quoted_amount ? ` (${formatCurrency(nextOption.quoted_amount, nextOption.currency || 'USD')})` : ''}.`,
      inquiry.status === 'SUBMITTED' ? 'QUALIFYING' : undefined,
      nextDocuments,
    )
    resetForm()
    setDialogOpen(false)
  }

  async function confirmSupplier(option: SupplierOption) {
    const nextStatus =
      inquiry.status === 'SUBMITTED'
        ? 'QUALIFYING'
        : inquiry.status === 'QUALIFYING'
          ? 'QUOTED'
          : undefined
    const nextAction =
      inquiry.status === 'SUBMITTED'
        ? 'Review supplier quotations and landed-cost assumptions'
        : 'Confirm selected supplier and place order'
    const nextOptions = options.map((current) => ({
      ...current,
      selected: current.id === option.id,
      quote_status: current.id === option.id ? 'CUSTOMER_APPROVED' : current.quote_status,
    }))
    await saveOptions(
      nextOptions,
      inquiry.status === 'ACTIVE' ? 'Place order and coordinate factory payment' : nextAction,
      `Customer approved supplier option: ${option.name}.`,
      nextStatus,
    )
  }

  async function unconfirmSupplier(option: SupplierOption) {
    const nextOptions = options.map((current) =>
      current.id === option.id
        ? {
            ...current,
            selected: false,
            quote_status:
              current.quote_status === 'CUSTOMER_APPROVED'
                ? 'QUOTE_RECEIVED'
                : current.quote_status,
          }
        : current,
    )
    await saveOptions(
      nextOptions,
      'Review supplier quotations and landed-cost assumptions',
      `Customer approval removed from supplier option: ${option.name}.`,
    )
  }

  return (
    <Card data-tour="inquiry-suppliers">
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Supplier Options</CardTitle>
            <CardDescription>
              Track factory candidates, quotation status, proforma links, and the customer-approved
              supplier.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{options.length} options</Badge>
            {admin && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => openSupplierDialog()}
                >
                  <PlusCircle className="h-4 w-4" />
                  Add Option
                </Button>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>
                      {editingSupplier ? 'Edit Supplier Option' : 'Add Supplier Option'}
                    </DialogTitle>
                    <DialogDescription>
                      Capture a factory candidate, quotation status, quote/proforma file, and
                      internal notes.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="supplier-option-name">Factory / Supplier</Label>
                      <Input
                        id="supplier-option-name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <HintedLabel
                        plain
                        htmlFor="supplier-option-status"
                        hint={FIELD_HELP.quoteStatus}
                      >
                        Quote Status
                      </HintedLabel>
                      <Select value={quoteStatus} onValueChange={setQuoteStatus} disabled={saving}>
                        <SelectTrigger id="supplier-option-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            'PROSPECT',
                            'QUOTE_REQUESTED',
                            'QUOTE_RECEIVED',
                            'PROFORMA_UPLOADED',
                            'CUSTOMER_APPROVED',
                            'REJECTED',
                          ].map((option) => (
                            <SelectItem key={option} value={option}>
                              {formatLabel(option)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier-option-email">Email</Label>
                      <Input
                        id="supplier-option-email"
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier-option-phone">Phone</Label>
                      <Input
                        id="supplier-option-phone"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <HintedLabel
                        plain
                        htmlFor="supplier-option-amount"
                        hint={FIELD_HELP.quotedAmount}
                      >
                        Quoted Amount
                      </HintedLabel>
                      <Input
                        id="supplier-option-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={quotedAmount}
                        onChange={(event) => setQuotedAmount(event.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier-option-currency">Currency</Label>
                      <Select value={currency} onValueChange={setCurrency} disabled={saving}>
                        <SelectTrigger id="supplier-option-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {quoteCurrencies.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <HintedLabel
                        plain
                        htmlFor="supplier-option-proforma"
                        hint={FIELD_HELP.proforma}
                      >
                        Proforma / Quotation URL
                      </HintedLabel>
                      <Input
                        id="supplier-option-proforma"
                        value={proformaUrl}
                        onChange={(event) => setProformaUrl(event.target.value)}
                        disabled={saving}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="supplier-option-file">Upload Quote / Proforma</Label>
                      <Input
                        id="supplier-option-file"
                        type="file"
                        accept="image/*,.pdf,.xls,.xlsx,.csv"
                        onChange={(event) => setQuoteFile(event.target.files?.[0] ?? null)}
                        disabled={saving}
                      />
                      {quoteFile && (
                        <p className="text-xs text-muted-foreground">{quoteFile.name}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="supplier-option-address">Factory Address</Label>
                      <Textarea
                        id="supplier-option-address"
                        rows={3}
                        value={address}
                        onChange={(event) => setAddress(event.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="supplier-option-notes">Notes</Label>
                      <Textarea
                        id="supplier-option-notes"
                        rows={3}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={saveSupplierOption}
                      disabled={saving || !name.trim()}
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : editingSupplier ? (
                        <Save className="h-4 w-4" />
                      ) : (
                        <PlusCircle className="h-4 w-4" />
                      )}
                      {editingSupplier ? 'Update Supplier Option' : 'Add Supplier Option'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {options.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            No supplier options have been captured for this inquiry yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border max-md:hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Quote</TableHead>
                  <TableHead>Proforma</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {options.map((option) => (
                  <TableRow key={option.id}>
                    <TableCell className="min-w-[260px]">
                      <div className="flex items-start gap-2">
                        <Factory className="mt-1 h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{option.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {[option.email, option.phone].filter(Boolean).join(' · ') ||
                              'No contact captured'}
                          </div>
                          {option.address && (
                            <div className="mt-1 max-w-[360px] text-xs text-muted-foreground">
                              {option.address}
                            </div>
                          )}
                          <Translatable
                            as="div"
                            text={option.notes}
                            context="supplier_quote"
                            className="mt-1 max-w-[360px] text-xs"
                            label={`the notes on ${option.name}`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {option.quoted_amount != null
                        ? formatCurrency(option.quoted_amount, option.currency || 'USD')
                        : 'Pending'}
                    </TableCell>
                    <TableCell>
                      {option.proforma_url ? (
                        <a
                          className="text-sm text-primary underline-offset-4 hover:underline"
                          href={option.proforma_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        'Not uploaded'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={option.selected ? 'secondary' : 'outline'}>
                          {option.selected ? 'Selected' : formatLabel(option.quote_status)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          disabled={!admin || saving}
                          onClick={() => openSupplierDialog(option)}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Button>
                        {option.selected ? (
                          <Button
                            type="button"
                            size="xs"
                            variant="outline"
                            disabled={!admin || saving}
                            onClick={() => unconfirmSupplier(option)}
                          >
                            <XCircle className="h-4 w-4" />
                            Unconfirm
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="xs"
                            variant="outline"
                            disabled={!admin || saving}
                            onClick={() => confirmSupplier(option)}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Confirm
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {options.length > 0 && (
          // On a phone, one card per supplier instead of a table wider than the screen.
          <ul aria-label="Supplier options" className="divide-y rounded-xl border md:hidden">
            {options.map((option) => (
              <li key={option.id} className="space-y-3 p-3">
                <div className="flex items-start gap-2">
                  <Factory className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 text-sm font-medium wrap-anywhere">{option.name}</p>
                      <Badge variant={option.selected ? 'secondary' : 'outline'}>
                        {option.selected ? 'Selected' : formatLabel(option.quote_status)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground wrap-anywhere">
                      {[option.email, option.phone].filter(Boolean).join(' · ') ||
                        'No contact captured'}
                    </p>
                    {option.address && (
                      <p className="text-xs text-muted-foreground wrap-anywhere">
                        {option.address}
                      </p>
                    )}
                    {option.notes && <p className="text-xs wrap-anywhere">{option.notes}</p>}
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Quote</dt>
                    <dd className="font-medium">
                      {option.quoted_amount != null
                        ? formatCurrency(option.quoted_amount, option.currency || 'USD')
                        : 'Pending'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Proforma</dt>
                    <dd>
                      {option.proforma_url ? (
                        <a
                          className="text-primary underline-offset-4 hover:underline"
                          href={option.proforma_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        'Not uploaded'
                      )}
                    </dd>
                  </div>
                </dl>
                {admin && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={saving}
                      onClick={() => openSupplierDialog(option)}
                      aria-label={`Edit ${option.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </Button>
                    {option.selected ? (
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        disabled={saving}
                        onClick={() => unconfirmSupplier(option)}
                      >
                        <XCircle className="h-4 w-4" />
                        Unconfirm
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        disabled={saving}
                        onClick={() => confirmSupplier(option)}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Confirm
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {!admin && (
          <p className="text-sm text-muted-foreground">
            Admin access is required to manage supplier options.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function ConvertPanel({ inquiry, profile }: { inquiry: Inquiry; profile: Profile | null }) {
  const navigate = useNavigate()
  const admin = isAdmin(profile)
  const [trackingNumber, setTrackingNumber] = useState('')
  const [insurance, setInsurance] = useState(false)
  const [converting, setConverting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function convert() {
    setConverting(true)
    setError(null)
    try {
      const result = await apiClient.convertInquiry(inquiry.id, {
        tracking_number: trackingNumber.trim() || null,
        insurance_opted: insurance,
      })
      navigate(`/consignments/${result.consignment.id}`)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to convert inquiry')
    } finally {
      setConverting(false)
    }
  }

  if (inquiry.operational_state === 'CANCELLED' && !inquiry.converted_consignment_id) {
    return (
      <Card data-tour="inquiry-convert">
        <CardHeader>
          <CardTitle>Convert</CardTitle>
          <CardDescription>
            A cancelled inquiry does not become a consignment. Reopen it first.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (inquiry.converted_consignment_id) {
    return (
      <Card data-tour="inquiry-convert">
        <CardHeader>
          <CardTitle>Consignment</CardTitle>
          <CardDescription>This inquiry has been converted.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to={`/consignments/${inquiry.converted_consignment_id}`}>
              <ExternalLink className="h-4 w-4" />
              Open Consignment
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-tour="inquiry-convert">
      <CardHeader>
        <CardTitle>Convert</CardTitle>
        <CardDescription>Create the operational consignment when work starts.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <Label htmlFor="tracking">Tracking Reference</Label>
          <Input
            id="tracking"
            value={trackingNumber}
            onChange={(event) => setTrackingNumber(event.target.value)}
            disabled={!admin || converting}
          />
        </div>
        <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
          <input
            type="checkbox"
            checked={insurance}
            onChange={(event) => setInsurance(event.target.checked)}
            disabled={!admin || converting}
          />
          Insurance opted
        </label>
        {admin ? (
          <Button
            type="button"
            onClick={convert}
            disabled={converting || inquiry.product_list.length === 0}
          >
            {converting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PackageCheck className="h-4 w-4" />
            )}
            Convert To Consignment
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Admin access is required to convert inquiries.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function InquiryCommunicationPanel({
  inquiry,
  communications,
  profile,
  onChanged,
}: {
  inquiry: Inquiry
  communications: HelmCommunication[]
  profile: Profile | null
  onChanged: () => void
}) {
  const admin = isAdmin(profile)
  const email = inquiry.customer?.email || ''
  const phone = normalizeWhatsAppPhone(inquiry.customer?.phone)
  const [channel, setChannel] = useState<'EMAIL' | 'WHATSAPP'>('EMAIL')
  const [templateKey, setTemplateKey] = useState<(typeof templates)[number]>('HELM_INQUIRY_UPDATE')
  const [subject, setSubject] = useState('Helm inquiry update')
  const [ccRecipients, setCcRecipients] = useState<string[]>([])
  const [attachments, setAttachments] = useState<HelmOutboundCommunicationAttachment[]>([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const [body, setBody] = useState('')
  const [send, setSend] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.createCommunication({
        customer_id: inquiry.customer_id,
        inquiry_id: inquiry.id,
        channel,
        status: send ? undefined : 'LOGGED',
        template_key: templateKey,
        template_variables: {
          customer_name: customerName(inquiry),
          // Whom the email greets; never their address ("Hello," without a name).
          greeting_name: customerGreetingName(inquiry.customer),
          service_type: serviceTypeLabel(inquiry).toLowerCase(),
          status: formatLabel(inquiry.status).toLowerCase(),
          next_action: inquiry.next_action || 'We will follow up with the next step.',
          missing_documents: 'the requested documents',
          total_due: formatCurrency(centsToMajor(inquiry.declared_value_cents), 'USD'),
          reference: inquiry.id.slice(0, 8),
          tracking_line: '',
          // Never the internal status: it is the team's note, not words for the client.
          blocker: inquiry.next_action || 'additional information is required',
        },
        recipient: channel === 'EMAIL' ? email : `+${phone}`,
        cc: channel === 'EMAIL' ? ccRecipients : [],
        subject: channel === 'EMAIL' ? subject : null,
        body: body.trim() || null,
        attachments: channel === 'EMAIL' ? attachments : [],
        send,
        metadata: {
          inquiry_status: inquiry.status,
          service_type: inquiry.service_type,
          service_types: inquiry.service_types,
        },
      })
      setBody('')
      if (channel === 'EMAIL') setAttachments([])
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to record communication')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card data-tour="inquiry-communication">
      <CardHeader>
        <CardTitle>Communications</CardTitle>
        <CardDescription>
          Replies drafted for you to approve, and templated email and WhatsApp updates.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {admin && <ReplyDrafts inquiryId={inquiry.id} onSent={onChanged} />}

        {admin ? (
          <form onSubmit={submit} className="space-y-4 rounded-md border p-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="channel">Channel</Label>
                <Select
                  value={channel}
                  onValueChange={(value) => setChannel(value as 'EMAIL' | 'WHATSAPP')}
                  disabled={submitting}
                >
                  <SelectTrigger id="channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="template">Template</Label>
                <Select
                  value={templateKey}
                  onValueChange={(value) => setTemplateKey(value as (typeof templates)[number])}
                  disabled={submitting}
                >
                  <SelectTrigger id="template">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template} value={template}>
                        {formatLabel(template)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Recipient</Label>
                <Input value={channel === 'EMAIL' ? email : `+${phone}`} disabled />
              </div>
            </div>

            {channel === 'EMAIL' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    disabled={submitting}
                  />
                </div>
                <HelmCcSelector
                  value={ccRecipients}
                  onChange={setCcRecipients}
                  disabled={submitting}
                  excludeEmails={[email]}
                />
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="body">Message Override</Label>
              <Textarea
                id="body"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                disabled={submitting}
                rows={5}
              />
            </div>

            {channel === 'EMAIL' && (
              <CommunicationAttachmentPicker
                value={attachments}
                onChange={setAttachments}
                onError={setError}
                onUploadingChange={setUploadingAttachments}
                disabled={submitting}
              />
            )}

            <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                checked={send}
                onChange={(event) => setSend(event.target.checked)}
                disabled={submitting}
              />
              Send through safe provider
            </label>

            <Button
              type="submit"
              disabled={
                submitting || uploadingAttachments || (channel === 'EMAIL' ? !email : !phone)
              }
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : channel === 'EMAIL' ? (
                <Mail className="h-4 w-4" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
              {send ? 'Send Update' : 'Log Update'}
            </Button>
          </form>
        ) : (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            Admin access is required to send updates.
          </p>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Timeline</h3>
            <Badge variant="outline">{communications.length}</Badge>
          </div>
          {communications.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">
              No communication events have been recorded for this inquiry.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {communications.map((communication) => {
                const cc = communicationCcRecipients(communication)
                return (
                  <div key={communication.id} className="space-y-2 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={communication.channel === 'WHATSAPP' ? 'secondary' : 'outline'}
                      >
                        {formatLabel(communication.channel)}
                      </Badge>
                      <Badge variant="outline">{formatLabel(communication.status)}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(communication.sent_at || communication.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">{communication.recipient}</div>
                    {cc.length > 0 && (
                      <div className="text-xs text-muted-foreground">CC: {cc.join(', ')}</div>
                    )}
                    <TranslatableMessage
                      subject={communication.subject}
                      body={communication.body}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function InquiryActivityLogPanel({
  inquiry,
  communications,
  staff,
}: {
  inquiry: Inquiry
  communications: HelmCommunication[]
  /** Staff read tasks by name; a customer's login does not. */
  staff: boolean
}) {
  const people = useHelmPeople()
  const activities = buildInquiryActivities(
    inquiry,
    communications,
    (userId) =>
      !userId
        ? null
        : userId === people.data?.meId
          ? 'You'
          : personName(people.data?.users.get(userId)),
    { taskTitles: staff },
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              Workflow, product, supplier, document, and communication events for this inquiry.
            </CardDescription>
          </div>
          <Badge variant="outline">{activities.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            No inquiry activity has been recorded yet.
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {activities.map((activity) => (
              <div key={activity.id} className="flex gap-3 p-4">
                <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground">
                  <History className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium">{activity.title}</div>
                    {activity.label && <Badge variant="outline">{activity.label}</Badge>}
                  </div>
                  {activity.timestamp && (
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(activity.timestamp)}
                    </div>
                  )}
                  <Translatable
                    text={activity.description}
                    context="activity"
                    className="whitespace-pre-wrap text-sm text-muted-foreground"
                    label={activity.title}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function InquiryDetailPage() {
  const { id } = useParams()
  const { data, loading, error, reload, refresh } = useAsyncData(async () => {
    if (!id) throw new Error('Inquiry id is missing')
    const [inquiry, profile, communications] = await Promise.all([
      apiClient.getInquiry(id),
      apiClient.getProfile().catch(() => null),
      apiClient.listCommunications({ inquiry_id: id, limit: 50 }).catch(() => []),
    ])
    return { inquiry, profile, communications }
  }, [id])

  if (loading) return <LoadingState label="Loading inquiry..." />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data?.inquiry) return <ErrorState error="Inquiry not found" />

  const { inquiry, profile, communications } = data
  const supplierOption = selectedSupplierOption(inquiry)
  // Tasks are the team's own: staff see them, a customer's login does not.
  const staff = Boolean(profile) && !isCustomerLogin(profile)
  // The Workflow card starts from the active task; when the Tasks list
  // changes which task that is, the card starts again from the new one.
  const activeTask =
    inquiry.tasks.find((task) => task.status === 'IN_PROGRESS') ??
    inquiry.tasks.find((task) => task.status === 'TODO')

  return (
    <div className="space-y-6">
      <PageHeader
        title={inquiryTitle(inquiry)}
        titleAddon={
          <>
            <StatusBadge status={inquiry.status} />
            <Badge variant="outline">{formatLabel(inquiry.priority)}</Badge>
          </>
        }
        description={`${customerName(inquiry)} · ${inquiry.reference_code ?? inquiry.id.slice(0, 8)}`}
        breadcrumbs={[
          { label: 'Inquiries', href: '/inquiries' },
          { label: inquiry.reference_code ?? 'Inquiry' },
        ]}
        actions={
          <>
            <InquiryOwnerPicker inquiry={inquiry} onChanged={reload} />
            {inquiry.converted_consignment_id && (
              <Button asChild className="self-end">
                <Link to={`/consignments/${inquiry.converted_consignment_id}`}>
                  <ExternalLink className="h-4 w-4" />
                  Consignment
                </Link>
              </Button>
            )}
          </>
        }
      />

      {inquiry.operational_state === 'CANCELLED' && (
        <InquiryCancelledBanner
          inquiry={inquiry}
          canReopen={isAdmin(profile)}
          onReopened={refresh}
        />
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <FactCard label="Status" value={<StatusBadge status={inquiry.status} />} />
        <FactCard
          label="Shipment"
          value={
            <ShipmentEditor
              inquiryId={inquiry.id}
              reference={inquiryTitle(inquiry)}
              shippingMethod={inquiry.shipping_method}
              airService={inquiry.air_service}
              canEdit={isAdmin(profile)}
              onChanged={refresh}
            />
          }
        />
        <FactCard
          label="Due"
          value={
            <DueDateEditor
              record={{
                kind: 'inquiry',
                id: inquiry.id,
                customerId: inquiry.customer_id,
                customer: inquiry.customer,
                reference: inquiryTitle(inquiry),
              }}
              dueAt={inquiry.sla_due_at}
              canEdit={isAdmin(profile)}
              onChanged={refresh}
            />
          }
        />
        <FactCard
          label="Value"
          value={formatCurrency(
            centsToMajor(inquiry.declared_value_cents ?? inquiry.budget_cents),
            'USD',
          )}
        />
      </div>

      {/* One column below xl; neither column is ever wider than the screen. */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          {staff && (
            <InquiryTasks inquiry={inquiry} canEdit={isAdmin(profile)} onChanged={refresh} />
          )}
          <InquiryProductsPanel inquiry={inquiry} profile={profile} onChanged={reload} />
          <SupplierOptionsPanel inquiry={inquiry} profile={profile} onChanged={reload} />
          <UpdateInquiryPanel
            key={activeTask ? `${activeTask.id}:${activeTask.updated_at}` : 'no-task'}
            inquiry={inquiry}
            profile={profile}
            onChanged={reload}
          />
          <InquiryCommunicationPanel
            inquiry={inquiry}
            communications={communications}
            profile={profile}
            onChanged={reload}
          />
          <InquiryActivityLogPanel
            inquiry={inquiry}
            communications={communications}
            staff={staff}
          />
        </div>

        <div className="min-w-0 space-y-6">
          <ConvertPanel inquiry={inquiry} profile={profile} />

          <ClientFiles
            documents={inquiry.documents}
            filedDocuments={inquiry.filed_documents}
            inquiryId={inquiry.id}
          />

          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Customer ID</Label>
                <p className="mt-1 font-medium wrap-anywhere">
                  {inquiry.customer?.customer_number || 'Not assigned'}
                </p>
                {inquiry.customer?.is_placeholder && (
                  <Badge variant="outline" className="mt-2">
                    Provisional identity
                  </Badge>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Name</Label>
                <p className="mt-1 font-medium wrap-anywhere">{customerName(inquiry)}</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Email</Label>
                <p className="mt-1 wrap-anywhere">
                  {inquiry.customer
                    ? customerIdentityValue(inquiry.customer, 'email') || 'Not confirmed'
                    : 'Not set'}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Phone</Label>
                <p className="mt-1 wrap-anywhere">
                  {inquiry.customer
                    ? customerIdentityValue(inquiry.customer, 'phone') || 'Not confirmed'
                    : 'Not set'}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/customers/${inquiry.customer_id}`}>Open Customer</Link>
              </Button>
              {isAdmin(profile) && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/customers/${inquiry.customer_id}#customer-details`}>
                    Edit customer details
                  </Link>
                </Button>
              )}
              {isAdmin(profile) && (
                <InquiryCustomerAssociation inquiry={inquiry} onChanged={reload} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Supplier</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Name</Label>
                <p className="mt-1 wrap-anywhere">
                  {supplierOption?.name || supplierField(inquiry, 'name') || 'Not set'}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Email</Label>
                <p className="mt-1 wrap-anywhere">
                  {supplierOption?.email || supplierField(inquiry, 'email') || 'Not set'}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Phone</Label>
                <p className="mt-1 wrap-anywhere">
                  {supplierOption?.phone || supplierField(inquiry, 'phone') || 'Not set'}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Address</Label>
                <p className="mt-1 wrap-anywhere">
                  {supplierOption?.address || supplierField(inquiry, 'address') || 'Not set'}
                </p>
              </div>
              {supplierOption?.selected && <Badge variant="secondary">Customer Approved</Badge>}
            </CardContent>
          </Card>

          <InquiryCargoCard inquiry={inquiry} />

          <Card>
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {inquiry.source && (
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Came in by</Label>
                  <p className="mt-1 wrap-anywhere">{formatLabel(inquiry.source)}</p>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Origin</Label>
                <p className="mt-1 wrap-anywhere">{inquiry.origin_country || 'Not set'}</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Destination</Label>
                <p className="mt-1 wrap-anywhere">{inquiry.destination_address || 'Not set'}</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Incoterms</Label>
                <p className="mt-1 wrap-anywhere">{inquiry.incoterms || 'Not set'}</p>
              </div>
              <Separator />
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Next Action</Label>
                <p className="mt-1 wrap-anywhere">
                  {isControlledInquiryNextAction(inquiry.next_action)
                    ? inquiry.next_action
                    : defaultInquiryNextAction(inquiry.status)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
